import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, Phone, Shield } from 'lucide-react';
import { formatPhoneForIndia } from '@/config/auth-config';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/context/AuthContext';

const RESEND_COOLDOWN_MS = 30000; // 30 seconds

const PhoneAuthPage = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  React.useEffect(() => {
    // Initialize recaptcha verifier
    if (!recaptchaVerifier) {
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'normal',
        callback: () => {
          // reCAPTCHA solved, allow signInWithPhoneNumber
        },
        'expired-callback': () => {
          // Response expired. Ask user to solve reCAPTCHA again.
        }
      });
      setRecaptchaVerifier(verifier);
    }

    return () => {
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
      }
    };
  }, [recaptchaVerifier]);

  const handleSendOtp = async () => {
    const formatted = formatPhoneForIndia(phone);

    if (!formatted.startsWith('+91') || formatted.length < 13) {
      alert('Please enter a valid Indian mobile number.');
      return;
    }

    if (!recaptchaVerifier) {
      alert('reCAPTCHA not initialized. Please refresh and try again.');
      return;
    }

    try {
      setLoading(true);

      const confirmation = await signInWithPhoneNumber(auth, formatted, recaptchaVerifier);
      setConfirmationResult(confirmation);
      setIsOtpSent(true);

      alert(`We've sent a verification code to ${formatted}`);
    } catch (error) {
      console.error('Error sending OTP:', error);
      alert(error.message || 'Failed to send OTP. Please try again.');
      
      // Clear reCAPTCHA on error
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
        window.location.reload(); // Refresh to reinitialize reCAPTCHA
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!confirmationResult) {
      alert('Please request a new OTP.');
      return;
    }

    if (!otp || otp.length !== 6) {
      alert('Please enter the 6-digit OTP.');
      return;
    }

    try {
      setLoading(true);
      
      const userCredential = await confirmationResult.confirm(otp);
      const firebaseUser = userCredential.user;

      // Create user state
      const newUserState = {
        customerId: firebaseUser.uid,
        guestSessionId: null,
        email: firebaseUser.email || firebaseUser.phoneNumber,
        uid: firebaseUser.uid,
        isAuthenticated: true,
        role: 'customer',
        phoneNumber: firebaseUser.phoneNumber,
      };

      setUser(newUserState);

      // Store in localStorage
      localStorage.setItem('userEmail', firebaseUser.email || firebaseUser.phoneNumber || '');
      localStorage.setItem('customerId', firebaseUser.uid);
      localStorage.setItem('userRole', 'customer');
      localStorage.setItem('authTimestamp', Date.now().toString());

      alert('Phone verified successfully!');
      navigate('/'); // Navigate to home
    } catch (error) {
      console.error('Error verifying OTP:', error);
      alert(error.message || 'Failed to verify code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendDisabled || loading) return;

    setResendDisabled(true);
    await handleSendOtp();

    setTimeout(() => {
      setResendDisabled(false);
    }, RESEND_COOLDOWN_MS);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-md">
        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <Card className="w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Phone className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {isOtpSent ? 'Verify OTP' : 'Sign in with Phone'}
            </CardTitle>
            <p className="text-muted-foreground">
              {isOtpSent
                ? `Enter the code sent to ${formatPhoneForIndia(phone)}`
                : 'Use your mobile number for quick, secure sign-in'}
            </p>
          </CardHeader>

          <CardContent className="space-y-4">
            {!isOtpSent ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\s+/g, ''))}
                    placeholder="Enter your mobile (India only)"
                    maxLength={10}
                  />
                </div>

                {/* reCAPTCHA container */}
                <div id="recaptcha-container" className="flex justify-center my-4"></div>

                <Button
                  onClick={handleSendOtp}
                  disabled={loading || !phone}
                  className="w-full"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="otp">Verification Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                  />
                </div>

                <Button
                  onClick={handleVerifyOtp}
                  disabled={loading || !otp}
                  className="w-full"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Verify & Continue
                    </>
                  )}
                </Button>

                <div className="text-center">
                  <Button
                    variant="link"
                    onClick={handleResendOtp}
                    disabled={resendDisabled || loading}
                    className={resendDisabled ? "opacity-50" : ""}
                  >
                    {resendDisabled ? 'Please wait...' : 'Resend Code'}
                  </Button>
                </div>
              </>
            )}

            <div className="mt-6 rounded-lg bg-muted p-4">
              <p className="text-sm text-muted-foreground text-center">
                We use your phone number only for sign-in and order updates.
                AuraKart will never share your number with third parties or spam you.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PhoneAuthPage;